import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import BookingService from '../services/booking.service';
import { castDateshort } from '../shared/utils/string';

const MODES = {
  NO_REFUND: 'NO_REFUND',
  TRANSFER_FULL_REFUND: 'TRANSFER_FULL_REFUND',
  TRANSFER_CUSTOM_REFUND: 'TRANSFER_CUSTOM_REFUND',
  CREDIT_FULL_REFUND: 'CREDIT_FULL_REFUND',
  CREDIT_CUSTOM_REFUND: 'CREDIT_CUSTOM_REFUND',
  HOTEL_BILLED: 'HOTEL_BILLED',
  HOTEL_NOT_BILLED: 'HOTEL_NOT_BILLED',
};

const DECISIONS = {
  NO_REFUND: 'NO_REFUND',
  REFUND: 'REFUND',
};

const CHANNELS = {
  TRANSFER: 'TRANSFER',
  CREDIT: 'CREDIT',
};

const OPTIONS = {
  FULL: 'FULL',
  CUSTOM: 'CUSTOM',
};

const primaryButtonSx = {
  backgroundColor: '#393D44',
  color: '#FAEF9B',
  textTransform: 'none',
  borderRadius: '8px',
  minHeight: '42px',
  boxShadow: '0 8px 18px rgba(57, 61, 68, 0.16)',
  '&:hover': {
    backgroundColor: '#2f3339',
    boxShadow: '0 10px 22px rgba(57, 61, 68, 0.2)',
  },
};

const secondaryButtonSx = {
  backgroundColor: '#FFFFFF',
  color: '#393D44',
  textTransform: 'none',
  borderRadius: '8px',
  minHeight: '42px',
  border: '1px solid #d7d7d2',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#f6f6f2',
    boxShadow: 'none',
  },
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    fontSize: '14px',
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
  },
};

const modalClassNames = {
  base: 'admin-flex-cancel-modal',
  afterOpen: 'admin-flex-cancel-modal--after-open',
  beforeClose: 'admin-flex-cancel-modal--before-close',
};

const overlayClassNames = {
  base: 'admin-flex-cancel-overlay',
  afterOpen: 'admin-flex-cancel-overlay--after-open',
  beforeClose: 'admin-flex-cancel-overlay--before-close',
};

function formatAmount(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString('fr-CH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCurrency(value) {
  return `${formatAmount(value)} CHF`;
}

function normalizeAmount(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function createIdempotencyKey() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function AdminFlexibleCancellationModal({
  isOpen,
  booking,
  api,
  onClose,
  onSuccess,
}) {
  const { t } = useTranslation(['dashboard']);
  const [preview, setPreview] = useState(null);
  const [decision, setDecision] = useState(DECISIONS.NO_REFUND);
  const [refundChannel, setRefundChannel] = useState(CHANNELS.TRANSFER);
  const [refundOption, setRefundOption] = useState(OPTIONS.FULL);
  const [hotelMode, setHotelMode] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [reason, setReason] = useState('');
  const [phase, setPhase] = useState('form');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const apiRef = useRef(api);
  const tRef = useRef(t);
  const loadedBookingRef = useRef(null);

  useEffect(() => {
    apiRef.current = api;
    tRef.current = t;
  });

  useEffect(() => {
    let mounted = true;

    if (!isOpen || !booking?.id) {
      loadedBookingRef.current = null;
      return () => {
        mounted = false;
      };
    }

    const loadKey = String(booking.id);
    if (loadedBookingRef.current === loadKey) {
      return () => {
        mounted = false;
      };
    }
    loadedBookingRef.current = loadKey;

    setPreview(null);
    setDecision(DECISIONS.NO_REFUND);
    setRefundChannel(CHANNELS.TRANSFER);
    setRefundOption(OPTIONS.FULL);
    setHotelMode('');
    setTransferAmount('');
    setCreditAmount('');
    setReason('');
    setPhase('form');
    setIdempotencyKey(createIdempotencyKey());
    setLoading(true);
    setError('');
    setSuccessMessage('');

    BookingService.getAdminCancelPreview(booking.id, apiRef.current)
      .then((response) => {
        if (mounted) {
          setPreview(response.data);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err?.response?.data?.message || tRef.current('dashboard:adminCancel.previewError'));
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [booking?.id, isOpen]);

  const refundableBalance = Number(preview?.refundable_online_balance || 0);
  const transferFullAmount = Number(preview?.transfer_full_refund_amount ?? normalizeAmount(refundableBalance * 0.96));
  const transferFeeAmount = normalizeAmount(refundableBalance - transferFullAmount);
  const creditMaxAmount = Number(preview?.credit_max_amount ?? preview?.total_fare ?? 0);
  const canTransfer = Boolean(preview?.eligible_modes?.[MODES.TRANSFER_FULL_REFUND])
    || Boolean(preview?.eligible_modes?.[MODES.TRANSFER_CUSTOM_REFUND]);
  const canCredit = Boolean(preview?.eligible_modes?.[MODES.CREDIT_FULL_REFUND])
    || Boolean(preview?.eligible_modes?.[MODES.CREDIT_CUSTOM_REFUND]);
  const canHotelBilling = Boolean(preview?.eligible_modes?.[MODES.HOTEL_BILLED])
    || Boolean(preview?.eligible_modes?.[MODES.HOTEL_NOT_BILLED]);
  const canStandardCancellation = Boolean(preview?.eligible_modes?.[MODES.NO_REFUND])
    || canTransfer
    || canCredit;
  const isHotelBilling = Boolean(preview?.is_hotel_billing)
    || (canHotelBilling && !canStandardCancellation);

  const selectedMode = useMemo(() => {
    if (isHotelBilling) {
      return hotelMode;
    }

    if (decision === DECISIONS.NO_REFUND) {
      return MODES.NO_REFUND;
    }

    if (refundChannel === CHANNELS.CREDIT) {
      return refundOption === OPTIONS.FULL
        ? MODES.CREDIT_FULL_REFUND
        : MODES.CREDIT_CUSTOM_REFUND;
    }

    return refundOption === OPTIONS.FULL
      ? MODES.TRANSFER_FULL_REFUND
      : MODES.TRANSFER_CUSTOM_REFUND;
  }, [decision, hotelMode, isHotelBilling, refundChannel, refundOption]);

  const calculatedAmount = useMemo(() => {
    if (isHotelBilling || decision === DECISIONS.NO_REFUND) {
      return 0;
    }

    if (refundChannel === CHANNELS.CREDIT) {
      return refundOption === OPTIONS.FULL
        ? creditMaxAmount
        : normalizeAmount(creditAmount);
    }

    return refundOption === OPTIONS.FULL
      ? transferFullAmount
      : normalizeAmount(transferAmount);
  }, [
    creditAmount,
    creditMaxAmount,
    decision,
    isHotelBilling,
    refundChannel,
    refundOption,
    transferAmount,
    transferFullAmount,
  ]);

  const estimatedRefundType = useMemo(() => {
    if (!selectedMode) {
      return '';
    }

    if (selectedMode === MODES.HOTEL_BILLED) {
      return 'BILLED_TO_HOTEL';
    }

    if (selectedMode === MODES.HOTEL_NOT_BILLED) {
      return 'NOT_BILLED_TO_HOTEL';
    }

    if (selectedMode === MODES.CREDIT_FULL_REFUND || selectedMode === MODES.CREDIT_CUSTOM_REFUND) {
      return 'FLATT_OFF';
    }

    if (selectedMode === MODES.TRANSFER_FULL_REFUND) {
      return 'FULL_REFUND';
    }

    if (selectedMode === MODES.TRANSFER_CUSTOM_REFUND) {
      return calculatedAmount >= refundableBalance ? 'FULL_REFUND' : 'PARTIAL_REFUND';
    }

    return 'NO_REFUND';
  }, [calculatedAmount, refundableBalance, selectedMode]);

  const transferAmountInvalid = selectedMode === MODES.TRANSFER_CUSTOM_REFUND
    && (calculatedAmount <= 0 || calculatedAmount > refundableBalance);
  const creditAmountInvalid = selectedMode === MODES.CREDIT_CUSTOM_REFUND
    && (calculatedAmount <= 0 || calculatedAmount > creditMaxAmount);
  const selectedModeEligible = selectedMode
    ? preview?.eligible_modes?.[selectedMode] !== false
    : false;
  const confirmDisabled = loading
    || !preview?.is_cancelable
    || !selectedMode
    || !selectedModeEligible
    || transferAmountInvalid
    || creditAmountInvalid;

  const bookingStatus = booking?.status || preview?.status || '';
  const statusLabel = bookingStatus
    ? t(`dashboard:status.${bookingStatus}`, { defaultValue: bookingStatus })
    : '-';
  const startLabel = booking?.start_before ? castDateshort(booking.start_before) : '-';
  const paymentTypeLabel = preview?.payment_type
    ? t(`dashboard:adminCancel.${preview.payment_type}`)
    : '-';
  const finalTypeLabel = estimatedRefundType
    ? t(`dashboard:refundType.${estimatedRefundType}`, { defaultValue: estimatedRefundType })
    : t('dashboard:adminCancel.selectDecision');
  const clientName = preview?.client_name || booking?.client?.firstname || '-';
  const hotelName = preview?.hotel_name || booking?.hotel?.name;
  const summaryItems = preview ? [
    {
      label: t('dashboard:adminCancel.bookingId'),
      value: `#${preview.booking_id}`,
    },
    {
      label: t('dashboard:adminCancel.client'),
      value: clientName,
    },
    {
      label: t('dashboard:adminCancel.status'),
      value: statusLabel,
      className: 'admin-flex-cancel__status-value',
    },
    {
      label: t('dashboard:adminCancel.start'),
      value: startLabel,
    },
  ] : [];

  if (hotelName) {
    summaryItems.splice(2, 0, {
      label: t('dashboard:adminCancel.hotel'),
      value: hotelName,
    });
  }

  const selectNoRefund = () => {
    setDecision(DECISIONS.NO_REFUND);
    setPhase('form');
    setError('');
  };

  const selectRefund = () => {
    setDecision(DECISIONS.REFUND);
    setPhase('form');
    setError('');

    if (!canTransfer && canCredit) {
      setRefundChannel(CHANNELS.CREDIT);
    }
  };

  const selectTransfer = (option) => {
    if (!canTransfer || loading) {
      return;
    }

    setDecision(DECISIONS.REFUND);
    setRefundChannel(CHANNELS.TRANSFER);
    setRefundOption(option);
    setPhase('form');
    setError('');
  };

  const selectCredit = (option) => {
    if (!canCredit || loading) {
      return;
    }

    setDecision(DECISIONS.REFUND);
    setRefundChannel(CHANNELS.CREDIT);
    setRefundOption(option);
    setPhase('form');
    setError('');
  };

  const openConfirmation = () => {
    if (confirmDisabled) {
      setError(t('dashboard:adminCancel.validationError'));
      return;
    }

    setError('');
    setPhase('confirm');
  };

  const submitCancellation = async () => {
    setLoading(true);
    setError('');

    const trimmedReason = reason.trim();
    const payload = {
      mode: selectedMode,
      idempotency_key: idempotencyKey,
    };

    if (trimmedReason) {
      payload.reason = trimmedReason;
    }

    if (selectedMode === MODES.TRANSFER_CUSTOM_REFUND || selectedMode === MODES.CREDIT_CUSTOM_REFUND) {
      payload.amount = calculatedAmount;
    }

    try {
      const response = await BookingService.adminCancel(booking.id, payload, api);
      setSuccessMessage(response.message || t('dashboard:adminCancel.success'));
      setPhase('success');
      if (onSuccess) {
        await onSuccess(response.data);
      }
    } catch (err) {
      setError(err?.response?.data?.message || t('dashboard:adminCancel.submitError'));
      setPhase('form');
    } finally {
      setLoading(false);
    }
  };

  const renderOptionButton = ({ selected, disabled, title, description, amount, onClick }) => (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      className={[
        'admin-flex-cancel__sub-option',
        selected ? 'admin-flex-cancel__sub-option--selected' : '',
      ].filter(Boolean).join(' ')}
    >
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      {amount && <em>{amount}</em>}
    </button>
  );

  const renderHotelDecision = () => (
    <section className="admin-flex-cancel__section">
      <div className="admin-flex-cancel__section-title">
        {t('dashboard:adminCancel.hotelBillingDecision')}
      </div>
      <div className="admin-flex-cancel__mode-grid admin-flex-cancel__mode-grid--two" role="radiogroup">
        {[
          {
            value: MODES.HOTEL_BILLED,
            icon: <ReceiptLongIcon fontSize="small" />,
            title: t('dashboard:adminCancel.modes.HOTEL_BILLED'),
            hint: t('dashboard:adminCancel.hotelBilledHint'),
          },
          {
            value: MODES.HOTEL_NOT_BILLED,
            icon: <CheckCircleIcon fontSize="small" />,
            title: t('dashboard:adminCancel.modes.HOTEL_NOT_BILLED'),
            hint: t('dashboard:adminCancel.hotelNotBilledHint'),
          },
        ].map((option) => {
          const disabled = preview?.eligible_modes?.[option.value] === false || loading;
          const selected = hotelMode === option.value;

          return (
            <button
              type="button"
              key={option.value}
              disabled={disabled}
              onClick={() => {
                setHotelMode(option.value);
                setPhase('form');
                setError('');
              }}
              aria-pressed={selected}
              className={[
                'admin-flex-cancel__mode',
                selected ? 'admin-flex-cancel__mode--selected' : '',
                disabled ? 'admin-flex-cancel__mode--disabled' : '',
              ].filter(Boolean).join(' ')}
            >
              <span className="admin-flex-cancel__mode-icon">{option.icon}</span>
              <span className="admin-flex-cancel__mode-copy">
                <strong>{option.title}</strong>
                <small>{option.hint}</small>
              </span>
              <span className="admin-flex-cancel__mode-radio" aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </section>
  );

  const renderRefundDecision = () => (
    <>
      <section className="admin-flex-cancel__section">
        <div className="admin-flex-cancel__section-title">
          {t('dashboard:adminCancel.refundDecision')}
        </div>
        <div className="admin-flex-cancel__choice-grid">
          <button
            type="button"
            disabled={loading || preview?.eligible_modes?.[MODES.NO_REFUND] === false}
            onClick={selectNoRefund}
            aria-pressed={decision === DECISIONS.NO_REFUND}
            className={[
              'admin-flex-cancel__choice',
              decision === DECISIONS.NO_REFUND ? 'admin-flex-cancel__choice--selected' : '',
            ].filter(Boolean).join(' ')}
          >
            <span className="admin-flex-cancel__choice-icon"><MoneyOffIcon fontSize="small" /></span>
            <span>
              <strong>{t('dashboard:adminCancel.noRefundTitle')}</strong>
              <small>{t('dashboard:adminCancel.noRefundHint')}</small>
            </span>
          </button>

          <button
            type="button"
            disabled={loading || (!canTransfer && !canCredit)}
            onClick={selectRefund}
            aria-pressed={decision === DECISIONS.REFUND}
            className={[
              'admin-flex-cancel__choice',
              decision === DECISIONS.REFUND ? 'admin-flex-cancel__choice--selected' : '',
            ].filter(Boolean).join(' ')}
          >
            <span className="admin-flex-cancel__choice-icon"><PaymentsIcon fontSize="small" /></span>
            <span>
              <strong>{t('dashboard:adminCancel.withRefundTitle')}</strong>
              <small>{t('dashboard:adminCancel.withRefundHint')}</small>
            </span>
          </button>
        </div>
      </section>

      {decision === DECISIONS.REFUND && (
        <section className="admin-flex-cancel__refund-grid">
          <div className={[
            'admin-flex-cancel__refund-panel',
            refundChannel === CHANNELS.TRANSFER ? 'admin-flex-cancel__refund-panel--selected' : '',
            !canTransfer ? 'admin-flex-cancel__refund-panel--disabled' : '',
          ].filter(Boolean).join(' ')}
          >
            <div className="admin-flex-cancel__refund-panel-head">
              <span className="admin-flex-cancel__mode-icon"><AccountBalanceIcon fontSize="small" /></span>
              <div>
                <strong>{t('dashboard:adminCancel.transferTitle')}</strong>
                <small>{canTransfer ? t('dashboard:adminCancel.transferHint') : t('dashboard:adminCancel.transferUnavailable')}</small>
              </div>
            </div>
            {renderOptionButton({
              selected: refundChannel === CHANNELS.TRANSFER && refundOption === OPTIONS.FULL,
              disabled: loading || !canTransfer,
              title: t('dashboard:adminCancel.fullTransferTitle'),
              description: t('dashboard:adminCancel.fullTransferHint', { fee: formatAmount(transferFeeAmount) }),
              amount: formatCurrency(transferFullAmount),
              onClick: () => selectTransfer(OPTIONS.FULL),
            })}
            {renderOptionButton({
              selected: refundChannel === CHANNELS.TRANSFER && refundOption === OPTIONS.CUSTOM,
              disabled: loading || !canTransfer,
              title: t('dashboard:adminCancel.customAmountTitle'),
              description: t('dashboard:adminCancel.transferCustomHint', { balance: formatAmount(refundableBalance) }),
              amount: transferAmount ? formatCurrency(calculatedAmount) : null,
              onClick: () => selectTransfer(OPTIONS.CUSTOM),
            })}
          </div>

          <div className={[
            'admin-flex-cancel__refund-panel',
            refundChannel === CHANNELS.CREDIT ? 'admin-flex-cancel__refund-panel--selected' : '',
            !canCredit ? 'admin-flex-cancel__refund-panel--disabled' : '',
          ].filter(Boolean).join(' ')}
          >
            <div className="admin-flex-cancel__refund-panel-head">
              <span className="admin-flex-cancel__mode-icon"><CardGiftcardIcon fontSize="small" /></span>
              <div>
                <strong>{t('dashboard:adminCancel.creditTitle')}</strong>
                <small>{canCredit ? t('dashboard:adminCancel.creditHint') : t('dashboard:adminCancel.creditUnavailable')}</small>
              </div>
            </div>
            {renderOptionButton({
              selected: refundChannel === CHANNELS.CREDIT && refundOption === OPTIONS.FULL,
              disabled: loading || !canCredit,
              title: t('dashboard:adminCancel.fullCreditTitle'),
              description: t('dashboard:adminCancel.fullCreditHint'),
              amount: formatCurrency(creditMaxAmount),
              onClick: () => selectCredit(OPTIONS.FULL),
            })}
            {renderOptionButton({
              selected: refundChannel === CHANNELS.CREDIT && refundOption === OPTIONS.CUSTOM,
              disabled: loading || !canCredit,
              title: t('dashboard:adminCancel.customAmountTitle'),
              description: t('dashboard:adminCancel.creditCustomHint', { balance: formatAmount(creditMaxAmount) }),
              amount: creditAmount ? formatCurrency(calculatedAmount) : null,
              onClick: () => selectCredit(OPTIONS.CUSTOM),
            })}
          </div>
        </section>
      )}

      {selectedMode === MODES.TRANSFER_CUSTOM_REFUND && (
        <section className="admin-flex-cancel__control-panel">
          <div className="admin-flex-cancel__control-heading">
            <span>{t('dashboard:adminCancel.transferCustomAmount')}</span>
            <button
              type="button"
              className="admin-flex-cancel__max"
              onClick={() => setTransferAmount(refundableBalance.toFixed(2))}
              disabled={loading || !canTransfer}
            >
              {t('dashboard:adminCancel.max')}
            </button>
          </div>
          <TextField
            label={t('dashboard:adminCancel.amount')}
            type="number"
            value={transferAmount}
            disabled={loading || !canTransfer}
            onChange={(event) => setTransferAmount(event.target.value)}
            inputProps={{ min: 0.01, max: refundableBalance, step: 0.01 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">CHF</InputAdornment>,
            }}
            error={transferAmountInvalid}
            helperText={transferAmountInvalid ? t('dashboard:adminCancel.transferAmountError') : t('dashboard:adminCancel.maxTransferBalance', { balance: formatAmount(refundableBalance) })}
            fullWidth
            sx={textFieldSx}
          />
        </section>
      )}

      {selectedMode === MODES.CREDIT_CUSTOM_REFUND && (
        <section className="admin-flex-cancel__control-panel">
          <div className="admin-flex-cancel__control-heading">
            <span>{t('dashboard:adminCancel.creditCustomAmount')}</span>
            <button
              type="button"
              className="admin-flex-cancel__max"
              onClick={() => setCreditAmount(creditMaxAmount.toFixed(2))}
              disabled={loading || !canCredit}
            >
              {t('dashboard:adminCancel.max')}
            </button>
          </div>
          <TextField
            label={t('dashboard:adminCancel.amount')}
            type="number"
            value={creditAmount}
            disabled={loading || !canCredit}
            onChange={(event) => setCreditAmount(event.target.value)}
            inputProps={{ min: 0.01, max: creditMaxAmount, step: 0.01 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">CHF</InputAdornment>,
            }}
            error={creditAmountInvalid}
            helperText={creditAmountInvalid ? t('dashboard:adminCancel.creditAmountError') : t('dashboard:adminCancel.maxCreditBalance', { balance: formatAmount(creditMaxAmount) })}
            fullWidth
            sx={textFieldSx}
          />
        </section>
      )}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={loading ? undefined : onClose}
      shouldCloseOnOverlayClick={!loading}
      className={modalClassNames}
      overlayClassName={overlayClassNames}
      contentLabel={t('dashboard:adminCancel.title')}
    >
      <div className="admin-flex-cancel">
        <div className="admin-flex-cancel__header">
          <div>
            <div className="admin-flex-cancel__eyebrow">{t('dashboard:adminCancel.eyebrow')}</div>
            <h2 className="admin-flex-cancel__title">{t('dashboard:adminCancel.title')}</h2>
            <p className="admin-flex-cancel__subtitle">
              {booking?.id ? t('dashboard:adminCancel.subtitle', { id: booking.id }) : ''}
            </p>
          </div>
          <Tooltip title={t('dashboard:close_button')}>
            <span>
              <IconButton
                aria-label={t('dashboard:close_button')}
                className="admin-flex-cancel__close"
                onClick={onClose}
                disabled={loading}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        {loading && !preview ? (
          <div className="admin-flex-cancel__loading">
            <CircularProgress size={30} sx={{ color: '#393D44' }} />
            <span>{t('dashboard:adminCancel.loading')}</span>
          </div>
        ) : !preview ? (
          <div className="admin-flex-cancel__empty">
            <WarningAmberIcon />
            <p>{error || t('dashboard:adminCancel.previewError')}</p>
            <Button variant="contained" onClick={onClose} sx={primaryButtonSx}>
              {t('dashboard:close_button')}
            </Button>
          </div>
        ) : phase === 'success' ? (
          <div className="admin-flex-cancel__success">
            <CheckCircleIcon />
            <h3>{t('dashboard:adminCancel.successTitle')}</h3>
            <p>{successMessage}</p>
            <Button variant="contained" onClick={onClose} sx={primaryButtonSx}>
              {t('dashboard:close_button')}
            </Button>
          </div>
        ) : (
          <>
            <div className="admin-flex-cancel__body">
              <section className="admin-flex-cancel__section">
                <div className="admin-flex-cancel__section-title">
                  {t('dashboard:adminCancel.bookingSection')}
                </div>
                <div className="admin-flex-cancel__summary-grid">
                  {summaryItems.map((item) => (
                    <div className="admin-flex-cancel__summary-item" key={item.label}>
                      <span>{item.label}</span>
                      <strong className={item.className || ''}>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              {!isHotelBilling && (
                <section className="admin-flex-cancel__money-grid" aria-label={t('dashboard:adminCancel.paymentSection')}>
                  <div className="admin-flex-cancel__money-item">
                    <span>{t('dashboard:adminCancel.paymentType')}</span>
                    <strong>{paymentTypeLabel}</strong>
                  </div>
                  <div className="admin-flex-cancel__money-item">
                    <span>{t('dashboard:adminCancel.totalFare')}</span>
                    <strong>{formatCurrency(preview.total_fare)}</strong>
                  </div>
                  <div className="admin-flex-cancel__money-item">
                    <span>{t('dashboard:adminCancel.alreadyRefunded')}</span>
                    <strong>{formatCurrency(preview.already_refunded_amount)}</strong>
                  </div>
                  <div className="admin-flex-cancel__money-item admin-flex-cancel__money-item--highlight">
                    <span>{t('dashboard:adminCancel.refundableBalance')}</span>
                    <strong>{formatCurrency(refundableBalance)}</strong>
                  </div>
                </section>
              )}

              {preview?.warnings?.length > 0 && (
                <div className="admin-flex-cancel__warning">
                  <div className="admin-flex-cancel__warning-title">
                    <WarningAmberIcon fontSize="small" />
                    <strong>{t('dashboard:adminCancel.warningTitle')}</strong>
                  </div>
                  {preview.warnings.map((warning) => (
                    <div className="admin-flex-cancel__warning-line" key={warning}>{warning}</div>
                  ))}
                </div>
              )}

              {isHotelBilling ? renderHotelDecision() : renderRefundDecision()}

              <section className="admin-flex-cancel__section">
                <div className="admin-flex-cancel__section-title">
                  {t('dashboard:adminCancel.reasonSection')}
                </div>
                <TextField
                  label={t('dashboard:adminCancel.reason')}
                  value={reason}
                  disabled={loading}
                  onChange={(event) => setReason(event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  sx={textFieldSx}
                />
              </section>

              {phase === 'confirm' && (
                <div className="admin-flex-cancel__confirmation">
                  <WarningAmberIcon fontSize="small" />
                  <span>
                    {isHotelBilling
                      ? t('dashboard:adminCancel.hotelConfirmation', { refundType: finalTypeLabel })
                      : t('dashboard:adminCancel.confirmation', {
                        amount: formatAmount(calculatedAmount),
                        refundType: finalTypeLabel,
                      })}
                  </span>
                </div>
              )}

              {error && <div className="admin-flex-cancel__error">{error}</div>}
            </div>

            <div className="admin-flex-cancel__footer">
              <div className="admin-flex-cancel__final">
                <span>{isHotelBilling ? t('dashboard:adminCancel.finalHotelBilling') : t('dashboard:adminCancel.finalRefund')}</span>
                <strong>{isHotelBilling ? finalTypeLabel : formatCurrency(calculatedAmount)}</strong>
                <em>{finalTypeLabel}</em>
              </div>
              <div className="admin-flex-cancel__actions">
                <Button variant="contained" onClick={onClose} disabled={loading} sx={secondaryButtonSx}>
                  {t('dashboard:cancel_button')}
                </Button>
                {phase === 'confirm' ? (
                  <Button variant="contained" onClick={submitCancellation} disabled={confirmDisabled} sx={primaryButtonSx}>
                    {loading ? t('dashboard:adminCancel.processing') : t('dashboard:adminCancel.confirm')}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={openConfirmation} disabled={confirmDisabled} sx={primaryButtonSx}>
                    {t('dashboard:adminCancel.review')}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
